import { useTranslation } from 'react-i18next';

import { Box, Flex, Link, Text } from '@bonusly/design-system';

import type { ApprovalDecision, UiBlock } from '../hooks/use-bizy-global-chat';
import { BizyActionButtons, type ActionButtonItem } from './bizy-action-buttons';
import { BizyApprovalCard } from './bizy-approval-card';
import { BizyContentCard } from './bizy-content-card';
import { BizyDraftCard, type DraftVariant } from './bizy-draft-card';
import { BizyRecognitionInline } from './bizy-recognition-inline';

interface BizyUiBlockRendererProps {
	blocks: UiBlock[];
	messageKey?: string;
	/** When rendering a subset of a message's blocks (e.g. inline splitting),
	 * the parent passes the block's original index so choice updates target
	 * the correct block in the message's uiBlocks array. */
	blockIndexOffset?: number;
	onConfirm?: (blockIndex: number, confirmMessage: string) => void;
	onChoiceUpdate?: (messageKey: string, blockIndex: number, choice: ApprovalDecision) => void;
}

export function BizyUiBlockRenderer({ blocks, messageKey, blockIndexOffset = 0, onConfirm, onChoiceUpdate }: BizyUiBlockRendererProps) {
	const { t } = useTranslation('admin_bizy');

	return (
		<>
			{blocks.map((block, localIndex) => {
				const blockIndex = localIndex + blockIndexOffset;
				if (block.type === 'approval_card') {
					const p = block.payload as {
						variant?: string;
						title?: string;
						recipients?: string[];
						amount?: number;
						message?: string;
						hashtags?: string[];
						footer?: string;
						metadata?: { key: string; value: string }[];
						confirmLabel?: string;
						confirmMessage?: string;
					};
					const isRec = p.variant === 'recognition';
					return (
						<Box key={`blk-${blockIndex}`} my={2}>
							<BizyApprovalCard
								title={p.title ?? t('approval_card.default_title')}
								metadata={!isRec ? p.metadata : undefined}
								footer={p.footer}
								confirmLabel={p.confirmLabel}
								receiptLabel={
									isRec && p.recipients?.length ? t('approval_card.recognition_sent', { recipients: p.recipients.map((r) => `@${r}`).join(', ') }) : undefined
								}
								choice={block.choice}
								onConfirm={() => {
									if (messageKey && onChoiceUpdate) onChoiceUpdate(messageKey, blockIndex, 'approved');
									if (p.confirmMessage && onConfirm) onConfirm(blockIndex, p.confirmMessage);
								}}
							>
								{isRec && <BizyRecognitionInline recipients={p.recipients} amount={p.amount} message={p.message} hashtags={p.hashtags} />}
							</BizyApprovalCard>
						</Box>
					);
				}

				if (block.type === 'content_card') {
					const p = block.payload as {
						variant?: string;
						contentType?: 'recognition' | 'meeting' | 'generic';
						title?: string;
						giverName?: string;
						recipients?: string[];
						amount?: number;
						message?: string;
						hashtags?: string[];
						date?: string;
						content?: string;
						href?: string;
						metadata?: { key: string; value: string }[];
					};

					if (p.variant === 'recognition') {
						return (
							<Box key={`blk-${blockIndex}`} my={2}>
								<Box border="1px solid" borderColor="border.soft" borderRadius={12} bg="bg.surface" overflow="hidden" px={3} py={3}>
									<Text fontSize="sm" color="text.default" lineHeight="tall">
										{p.giverName && (
											<Text as="span" fontSize="inherit" fontWeight="semibold">
												{p.giverName}:{' '}
											</Text>
										)}
										<BizyRecognitionInline recipients={p.recipients} amount={p.amount} message={p.message} hashtags={p.hashtags} />
									</Text>
									<Flex align="center" justify="space-between" mt={2}>
										<Text fontSize="xs" color="text.softest">
											{p.date}
										</Text>
										{p.href && (
											<Link href={p.href} fontSize="xs" color="text.interactive" target="_blank" rel="noopener noreferrer">
												{t('approval_card.view')}
											</Link>
										)}
									</Flex>
								</Box>
							</Box>
						);
					}

					return (
						<Box key={`blk-${blockIndex}`} my={2}>
							<BizyContentCard type={p.contentType ?? 'generic'} title={p.title} content={p.content ?? ''} href={p.href} metadata={p.metadata} />
						</Box>
					);
				}

				if (block.type === 'action_buttons') {
					const p = block.payload as { buttons?: ActionButtonItem[] };
					if (!p.buttons?.length) return null;
					return (
						<Box key={`blk-${blockIndex}`} my={2}>
							<BizyActionButtons buttons={p.buttons} onAction={(msg) => onConfirm?.(blockIndex, msg)} />
						</Box>
					);
				}

				if (block.type === 'draft_card') {
					const p = block.payload as {
						variants?: DraftVariant[];
						confirmLabel?: string;
						confirmMessage?: string;
					};
					if (!p.variants?.length) return null;
					return (
						<Box key={`blk-${blockIndex}`} my={2}>
							<BizyDraftCard
								variants={p.variants}
								confirmLabel={p.confirmLabel}
								choice={block.choice}
								onConfirm={() => {
									// BizyDraftCard calls onConfirm for both Edit-in-Give-Box and
									// Give Now, so this is the single place that records the choice.
									if (messageKey && onChoiceUpdate) onChoiceUpdate(messageKey, blockIndex, 'approved');
								}}
								onGiveNow={(msg) => {
									// Choice was already recorded by onConfirm (called first from
									// handleGiveNow); don't duplicate the state update.
									if (onConfirm) onConfirm(blockIndex, msg);
								}}
							/>
						</Box>
					);
				}

				return null;
			})}
		</>
	);
}
